import { Badge } from "@acme/ui/badge";
import { Button } from "@acme/ui/button";
import { Card } from "@acme/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@acme/ui/dropdown-menu";
import { Input } from "@acme/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@acme/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MoreHorizontal, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  statBadgeAmber,
  statBadgeGreen,
  statBadgeNeutral,
} from "~/common/components/stat-badge-styles.js";
import { ListViewToggle } from "~/common/components/list-view-toggle.js";
import { ListViewCardsGrid } from "~/common/components/responsive-list-view.js";
import { surfaceTableShell } from "~/common/components/surface-styles.js";
import { useListViewMode } from "~/common/hooks/use-list-view-mode.js";
import { api } from "~/common/utils/trpc.js";
import { CwNotificationSettingsDialog } from "~/features/clan-war-notifications/_components/cw-notification-settings-dialog.js";
import { CwNotificationsSwitch } from "~/features/clan-war-notifications/_components/cw-notifications-switch.js";
import ClanIdentityLink from "~/common/components/clan-identity-link.js";
import type { ConnectionItem } from "../_components/-connection-card.js";
import { ConnectionCard } from "../_components/-connection-card.js";

export const Route = createFileRoute("/_authed/my-players/$tag/clans")({
  component: ClansTab,
});

function ClansTab() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const queryClient = useQueryClient();
  const { effectiveView, viewMode, setViewMode, isBelowLg } = useListViewMode();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTagToFetch, setSearchTagToFetch] = useState("");
  const [notificationDialogClanTag, setNotificationDialogClanTag] = useState<string | null>(null);

  const overviewQueryOptions = api.player.getOverviewItemByTag.queryOptions(
    { playerTag: tag },
    { enabled: tag.length > 0 }
  );
  const { data: overview } = useQuery(overviewQueryOptions);

  const connectionsQueryOptions = api.player.getAllConnectionsToClansByTag.queryOptions(
    { playerTag: tag },
    { placeholderData: (previousData) => previousData }
  );
  const { data, isLoading: isConnectionsLoading } = useQuery(connectionsQueryOptions);

  const emptyConnections = {
    connected: [] as const,
    connection_requested: [] as const,
  };
  const connections = data ?? emptyConnections;
  const allClans = useMemo(
    () => [...connections.connected, ...connections.connection_requested],
    [connections.connected, connections.connection_requested]
  );

  const clanTagsInConnections = useMemo(() => new Set(allClans.map((c) => c.clanTag)), [allClans]);
  const canRequestSearchedTag = searchTagToFetch.length >= 5 && !clanTagsInConnections.has(searchTagToFetch);

  const requestConnectionMutationOptions = api.shared.connections.requestOrAccept.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: api.player.getAllConnectionsToClansByTag.queryKey({
          playerTag: tag,
        }),
      });
    },
  });
  const requestConnection = useMutation(requestConnectionMutationOptions);

  const removeConnectionMutationOptions = api.shared.connections.remove.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: api.player.getAllConnectionsToClansByTag.queryKey({
          playerTag: tag,
        }),
      });
    },
  });
  const removeConnection = useMutation(removeConnectionMutationOptions);

  const toggleCwNotificationsMutationOptions = api.player.toggleCwNotifications.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: api.player.getAllConnectionsToClansByTag.queryKey({
          playerTag: tag,
        }),
      });
    },
  });
  const toggleCwNotifications = useMutation(toggleCwNotificationsMutationOptions);

  const getConnectionStatusForDialog = (clanTag: string) => {
    const conn = allClans.find((c) => c.clanTag === clanTag);
    return conn?.status ?? "connection_requested";
  };

  const displayTag = tag.startsWith("#") ? tag : `#${tag}`;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold">
          {overview != null ? t("app.connections.title") : t("app.connections.titleWithTag", { tag: displayTag })}
        </h2>
        <ListViewToggle isBelowLg={isBelowLg} setViewMode={setViewMode} viewMode={viewMode} />
      </div>

      <div className="space-y-6">
        <section aria-labelledby="add-clan-heading" className="space-y-3">
          <h2 className="text-lg font-semibold" id="add-clan-heading">
            {t("app.connections.addClan")}
          </h2>
          <div className="flex items-center gap-2">
            <Input
              className="min-w-0 flex-1"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("app.connections.searchPlaceholder")}
              value={searchQuery}
            />
            <Button className="shrink-0" onClick={() => setSearchTagToFetch(searchQuery.trim())} type="button">
              {t("app.connections.search")}
            </Button>
          </div>
          {searchTagToFetch.length >= 5 && (
            <div className="rounded-lg bg-card p-4 shadow-md dark:shadow-lg dark:shadow-black/35">
              {!canRequestSearchedTag ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={statBadgeNeutral} variant="outline">
                    {t("app.connections.alreadyConnected")}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{t("app.connections.alreadyConnectedHint")}</span>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <Badge className={statBadgeGreen} variant="outline">
                      {t("app.connections.clanFound")}
                    </Badge>
                    <span className="truncate font-medium font-mono text-sm">{searchTagToFetch}</span>
                  </div>
                  <Button
                    className="w-full sm:w-auto shrink-0"
                    disabled={requestConnection.isPending}
                    onClick={() =>
                      requestConnection.mutate({
                        playerTag: tag,
                        clanTag: searchTagToFetch,
                      })
                    }
                    size="sm"
                    variant="default">
                    {t("app.connections.requestConnection")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>

        {effectiveView === "table" ? (
          <div className={surfaceTableShell}>
            {isConnectionsLoading ? (
              <div className="p-8 text-center text-muted-foreground">{t("app.connections.loading")}</div>
            ) : allClans.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">{t("app.connections.empty")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("app.connections.table.clanName")}</TableHead>
                    <TableHead>{t("app.connections.table.level")}</TableHead>
                    <TableHead>{t("app.connections.table.cwNotifications")}</TableHead>
                    <TableHead>{t("app.connections.table.connectionStatus")}</TableHead>
                    <TableHead className="text-right">{t("app.connections.table.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allClans.map((connection) => (
                    <TableRow key={connection.clanTag}>
                      <TableCell className="font-medium">
                        <ClanIdentityLink
                          badgeUrls={connection.clanBadgeUrls}
                          clanName={connection.clanName}
                          clanTag={connection.clanTag}
                        />
                      </TableCell>
                      <TableCell>{connection.clanLevel ?? "–"}</TableCell>
                      <TableCell>
                        <CwNotificationsSwitch
                          checked={connection.cwNotificationsActive}
                          onCheckedChange={(enabled) =>
                            toggleCwNotifications.mutate({
                              playerTag: tag,
                              clanTag: connection.clanTag,
                              enabled,
                            })
                          }
                          showLabel={false}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            connection.status === "connected" ? statBadgeGreen : statBadgeAmber
                          }
                          variant="outline">
                          {connection.status === "connected"
                            ? t("app.connectionStatus.connected")
                            : t("app.connectionStatus.requested")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button className="h-8 w-8 p-0" variant="ghost">
                                <span className="sr-only">{t("app.myPlayers.openMenu")}</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setNotificationDialogClanTag(connection.clanTag)}>
                                {t("app.connections.cwSettings")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={removeConnection.isPending}
                                onClick={() =>
                                  removeConnection.mutate({
                                    playerTag: tag,
                                    clanTag: connection.clanTag,
                                  })
                                }
                                variant="destructive">
                                <Trash2 className="size-4" />
                                {connection.status === "connected"
                                  ? t("app.connections.removeConnection")
                                  : t("app.connections.cancelRequest")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        ) : isConnectionsLoading ? (
          <ListViewCardsGrid>
            {Array.from({ length: 4 }).map((_, i) => (
              <Card className="overflow-hidden" key={i}>
                <div className="p-4 space-y-3">
                  <div className="h-5 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
                  <div className="h-8 w-20 rounded bg-muted animate-pulse" />
                </div>
              </Card>
            ))}
          </ListViewCardsGrid>
        ) : allClans.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">{t("app.connections.empty")}</div>
        ) : (
          <ListViewCardsGrid>
            {allClans.map((connection: ConnectionItem) => (
              <ConnectionCard
                connection={connection}
                isRemovePending={removeConnection.isPending}
                key={connection.clanTag}
                onOpenNotificationSettings={setNotificationDialogClanTag}
                onRemoveConnection={(args) => removeConnection.mutate(args)}
                onToggleCwNotifications={(args) => toggleCwNotifications.mutate(args)}
                playerTag={tag}
              />
            ))}
          </ListViewCardsGrid>
        )}
      </div>

      {notificationDialogClanTag && (
        <CwNotificationSettingsDialog
          clanTag={notificationDialogClanTag}
          connectionStatus={getConnectionStatusForDialog(notificationDialogClanTag)}
          onOpenChange={(open) => {
            if (!open) setNotificationDialogClanTag(null);
          }}
          open={Boolean(notificationDialogClanTag)}
          playerTag={tag}
        />
      )}
    </>
  );
}
